import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { authAPI } from '../../services/api';
import logoImg from '../../assets/logo.png';
import templeImg from '../../assets/temple-far1.jpg';
import './AuthTheme.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function useTimer() {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (t <= 0) return;
    const id = setTimeout(() => setT(n => n - 1), 1000);
    return () => clearTimeout(id);
  }, [t]);
  return [t, setT];
}

const post = async (path, body) => {
  const res = await fetch(`${API}/auth/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
};

function Password({ show, onToggle, ...props }) {
  return (
    <div className="au-wrap">
      <input className="au-in" type={show ? 'text' : 'password'} {...props} />
      <button type="button" className="au-eye" onClick={onToggle} aria-label={show ? 'Hide password' : 'Show password'}>
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

export default function LoginPage() {
  const { login, loginWithData } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('email');       // email | phone
  const [step, setStep] = useState(1);           // phone: 1 number, 2 code
  const [form, setForm] = useState({ email: '', password: '' });
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [fails, setFails] = useState(0);
  const [lock, setLock] = useTimer();
  const [resend, setResend] = useTimer();
  const [fpWait, setFpWait] = useTimer();
  const [fp, setFp] = useState(null);            // reset-password modal state, null when closed

  const say = (type, text) => setMsg({ type, text });
  const switchTab = t => { setTab(t); setStep(1); setOtp(''); setMsg(null); };

  /* ── Email + password ── */
  const emailLogin = async e => {
    e.preventDefault(); setMsg(null);
    if (lock) return;
    setBusy(true);
    try {
      await login(form.email, form.password);
      navigate('/prelims');
    } catch (err) {
      const n = fails + 1;
      if (n >= 5) { setFails(0); setLock(30); say('err', 'Too many attempts. Wait 30 seconds.'); }
      else { setFails(n); say('err', err.response?.data?.message || 'Login failed.'); }
    } finally { setBusy(false); }
  };

  /* ── Phone OTP ── */
  const sendPhoneOtp = async e => {
    e?.preventDefault(); setMsg(null); setBusy(true);
    try {
      const d = await post('send-phone-otp', { phone });
      if (!d.success) { say('err', d.message); return; }
      say('ok', d.message); setStep(2); setResend(60);
    } catch { say('err', 'Network error.'); }
    finally { setBusy(false); }
  };

  const verifyPhoneOtp = async e => {
    e.preventDefault(); setMsg(null); setBusy(true);
    try {
      const d = await post('verify-phone-otp', { phone, otp });
      if (!d.success) { say('err', d.message); return; }
      loginWithData(d.token, d.user);
      navigate('/prelims');
    } catch { say('err', 'Network error.'); }
    finally { setBusy(false); }
  };

  /* ── Reset password ── */
  const fpOpen = !!fp;
  const patch = p => setFp(s => ({ ...s, ...p }));
  const openFp = () => setFp({ step: 1, email: form.email, otp: '', token: '', pass: '', confirm: '', err: '', busy: false, show: false });

  useEffect(() => {
    if (!fpOpen) return;
    const onKey = e => { if (e.key === 'Escape') setFp(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fpOpen]);

  const fpSend = async e => {
    e?.preventDefault(); patch({ err: '', busy: true });
    try {
      await authAPI.forgotPassword({ email: fp.email });
      patch({ step: 2, busy: false }); setFpWait(60);
    } catch (err) { patch({ err: err.response?.data?.message || 'Something went wrong.', busy: false }); }
  };

  const fpVerify = async e => {
    e.preventDefault(); patch({ err: '', busy: true });
    try {
      const { data } = await authAPI.verifyResetOtp({ email: fp.email, otp: fp.otp });
      patch({ token: data.resetToken, step: 3, busy: false });
    } catch (err) { patch({ err: err.response?.data?.message || 'Invalid or expired code.', busy: false }); }
  };

  const fpReset = async e => {
    e.preventDefault();
    if (fp.pass !== fp.confirm) { patch({ err: 'Passwords do not match.' }); return; }
    if (fp.pass.length < 8) { patch({ err: 'Use at least 8 characters.' }); return; }
    patch({ err: '', busy: true });
    try {
      await authAPI.resetPassword({ email: fp.email, resetToken: fp.token, newPassword: fp.pass });
      setForm({ email: fp.email, password: '' });
      switchTab('email'); say('ok', 'Password updated. Sign in.'); setFp(null);
    } catch (err) { patch({ err: err.response?.data?.message || 'Could not reset password.', busy: false }); }
  };

  return (
    <div className="au">
      <div className="au-card">
        <div className="au-art"><img src={templeImg} alt="" /></div>

        <div className="au-form">
          <img className="au-logo" src={logoImg} alt="CivilFox" />

          <div className="au-tabs">
            <button type="button" className={tab === 'email' ? 'on' : ''} onClick={() => switchTab('email')}>Email</button>
            <button type="button" className={tab === 'phone' ? 'on' : ''} onClick={() => switchTab('phone')}>Phone</button>
          </div>

          {msg && <p className={`au-msg ${msg.type}`} role="alert">{msg.text}</p>}

          {tab === 'email' && (
            <form className="au-fields" onSubmit={emailLogin}>
              <input className="au-in" type="email" placeholder="Email" aria-label="Email" autoComplete="email" required
                value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              <Password placeholder="Password" aria-label="Password" autoComplete="current-password" required
                show={show} onToggle={() => setShow(s => !s)}
                value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
              <button className="au-btn" type="submit" disabled={busy || lock > 0}>
                {lock > 0 ? `Retry in ${lock}s` : busy ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          )}

          {tab === 'phone' && step === 1 && (
            <form className="au-fields" onSubmit={sendPhoneOtp}>
              <input className="au-in" type="tel" placeholder="Phone" aria-label="Phone" required
                value={phone} onChange={e => setPhone(e.target.value)} />
              <button className="au-btn" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send code'}</button>
            </form>
          )}

          {tab === 'phone' && step === 2 && (
            <form className="au-fields" onSubmit={verifyPhoneOtp}>
              <input className="au-in au-otp" inputMode="numeric" maxLength={6} placeholder="000000" aria-label="Code" required
                value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} />
              <button className="au-btn" type="submit" disabled={busy || otp.length !== 6}>{busy ? 'Verifying…' : 'Verify'}</button>
              <div className="au-links">
                <button type="button" className="au-link" disabled={resend > 0} onClick={sendPhoneOtp}>
                  {resend > 0 ? `Resend in ${resend}s` : 'Resend'}
                </button>
                <button type="button" className="au-link" onClick={() => { setStep(1); setOtp(''); setMsg(null); }}>Change number</button>
              </div>
            </form>
          )}

          <div className="au-links">
            {tab === 'email' && <button type="button" className="au-link" onClick={openFp}>Forgot password</button>}
            <Link to="/register" className="au-link">Create account</Link>
          </div>
        </div>
      </div>

      {fp && (
        <div className="au-overlay" onClick={() => setFp(null)}>
          <div className="au-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Reset password">
            <button className="au-x" type="button" onClick={() => setFp(null)} aria-label="Close"><X size={18} /></button>
            {fp.err && <p className="au-msg err" role="alert">{fp.err}</p>}

            {fp.step === 1 && (
              <form className="au-fields" onSubmit={fpSend}>
                <input className="au-in" type="email" placeholder="Email" aria-label="Email" required autoFocus
                  value={fp.email} onChange={e => patch({ email: e.target.value })} />
                <button className="au-btn" type="submit" disabled={fp.busy}>{fp.busy ? 'Sending…' : 'Send code'}</button>
              </form>
            )}

            {fp.step === 2 && (
              <form className="au-fields" onSubmit={fpVerify}>
                <input className="au-in au-otp" inputMode="numeric" maxLength={6} placeholder="000000" aria-label="Code" required autoFocus
                  value={fp.otp} onChange={e => patch({ otp: e.target.value.replace(/\D/g, '') })} />
                <button className="au-btn" type="submit" disabled={fp.busy || fp.otp.length !== 6}>{fp.busy ? 'Verifying…' : 'Verify'}</button>
                <div className="au-links">
                  <button type="button" className="au-link" disabled={fpWait > 0} onClick={fpSend}>
                    {fpWait > 0 ? `Resend in ${fpWait}s` : 'Resend'}
                  </button>
                  <button type="button" className="au-link" onClick={() => patch({ step: 1, otp: '', err: '' })}>Change email</button>
                </div>
              </form>
            )}

            {fp.step === 3 && (
              <form className="au-fields" onSubmit={fpReset}>
                <Password placeholder="New password" aria-label="New password" required autoFocus
                  show={fp.show} onToggle={() => patch({ show: !fp.show })}
                  value={fp.pass} onChange={e => patch({ pass: e.target.value })} />
                <input className="au-in" type={fp.show ? 'text' : 'password'} placeholder="Confirm password" aria-label="Confirm password" required
                  value={fp.confirm} onChange={e => patch({ confirm: e.target.value })} />
                <button className="au-btn" type="submit" disabled={fp.busy}>{fp.busy ? 'Updating…' : 'Update password'}</button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}